import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '../api';
import {
  beginFile, drop, enqueue, fail, nextDraft, progress, retry,
  type Draft, type DraftFile,
} from './outbox';
import { sendDraft } from './sendDraft';

/*
 * Состояние очереди отправки и её ход. Живёт в ChatProvider, над всеми
 * разделами: уход в другой чат, в календарь или в звонок загрузку не рвёт.
 * Перезагрузка страницы очередь теряет — так решено, в хранилище её не кладём.
 */

export type EnqueueInput = {
  channelId: string;
  parentId: number | null;
  text: string;
  files: File[];
  replyToId?: number;
  /** Быстрый путь в открытую ленту; то же сообщение приезжает и потоком. */
  onSent?: (m: ChatMessage) => void;
  /** Голосовое: длительность и форма волны сняты при записи, не из файла. */
  voice?: { seconds: number; waveform: number[] };
};

let counter = 0;
const nextKey = () => {
  counter += 1;
  return `d${counter}-${Date.now()}`;
};

const toDraftFile = (file: File): DraftFile => ({
  key: nextKey(),
  file,
  name: file.name,
  mime: file.type,
  size: file.size,
  localUrl: URL.createObjectURL(file),
});

export function useOutbox() {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  /* Функции в очередь не кладём: она должна остаться простыми данными. */
  const callbacks = useRef(new Map<string, (m: ChatMessage) => void>());
  const busy = useRef(false);
  /*
   * Номера сообщений, доставленных очередью. Черновик уже проиграл движение
   * появления сам — настоящему сообщению, что приезжает ему на замену через
   * onSent или поток, повторно играть его не нужно (находка 2). Набор растил
   * бы память вечно, поэтому clearSettled чистит его при смене переписки —
   * тем же движением, что и свой набор новых строк чистит useFreshRows.
   */
  const [settledIds, setSettledIds] = useState<ReadonlySet<number>>(() => new Set());
  const clearSettled = useCallback(() => setSettledIds(new Set()), []);

  const enqueueDraft = useCallback((input: EnqueueInput) => {
    const key = nextKey();
    if (input.onSent) callbacks.current.set(key, input.onSent);
    setDrafts((q) =>
      enqueue(q, {
        key,
        channelId: input.channelId,
        parentId: input.parentId,
        replyToId: input.replyToId,
        text: input.text,
        files: input.files.map(toDraftFile),
        ts: Date.now(),
        state: 'ждёт',
        fileIndex: 0,
        pct: 0,
        error: '',
        voice: input.voice,
      }),
    );
  }, []);

  const retryDraft = useCallback((key: string) => setDrafts((q) => retry(q, key)), []);

  const dropDraft = useCallback((key: string) => {
    const draft = drafts.find((d) => d.key === key);
    // Если черновик в состоянии 'идёт', не трогаем его: отправка уже идёт,
    // и убирать его из ленты, пока сеть не завершила передачу, хуже, чем оставить.
    if (draft?.state === 'идёт') return;

    const toRevoke = draft?.files ?? [];
    setDrafts((q) => drop(q, key));
    toRevoke.forEach((f) => URL.revokeObjectURL(f.localUrl));
    callbacks.current.delete(key);
  }, [drafts]);

  useEffect(() => {
    if (busy.current) return;
    const draft = nextDraft(drafts);
    if (!draft) return;
    busy.current = true;
    void (async () => {
      try {
        const message = await sendDraft(draft, {
          begin: (i) => setDrafts((q) => beginFile(q, draft.key, i)),
          progress: (pct) => setDrafts((q) => progress(q, draft.key, pct)),
        });
        draft.files.forEach((f) => URL.revokeObjectURL(f.localUrl));
        setDrafts((q) => drop(q, draft.key));
        setSettledIds((prev) => new Set(prev).add(message.id));
        callbacks.current.get(draft.key)?.(message);
        callbacks.current.delete(draft.key);
      } catch (e) {
        setDrafts((q) => fail(q, draft.key, e instanceof Error ? e.message : 'Не удалось отправить'));
      } finally {
        busy.current = false;
      }
    })();
  }, [drafts]);

  return { drafts, enqueueDraft, retryDraft, dropDraft, settledIds, clearSettled };
}
